import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Form component for creating and editing comments
 */
const CommentForm = ({
  onSubmit,
  onCancel,
  initialContent = '',
  isReply = false,
  placeholder = 'Type your comment...',
  submitLabel = 'Comment',
  cancelLabel = 'Cancel',
  className = '',
  maxLength = 1000
}) => {
  const [content, setContent] = useState(initialContent);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);

  // Auto-focus and resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      autoResize(textareaRef.current);
    }
  }, []);

  /**
   * Auto-resize textarea based on content
   */
  const autoResize = (textarea) => {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  /**
   * Handle content change
   */
  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    autoResize(e.target);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedContent = content.trim();
    if (!trimmedContent) return;

    setIsSubmitting(true);
    try {
      await onSubmit(trimmedContent);
      setContent('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    setContent(initialContent);
    onCancel && onCancel();
  };

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit(e);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const characterCount = content.length;
  const isOverLimit = characterCount > maxLength;
  const canSubmit = content.trim().length > 0 && !isOverLimit && !isSubmitting;

  return (
    <form
      onSubmit={handleSubmit}
      className={`comment-form ${className}`}
      style={{
        backgroundColor: 'var(--comment-bg, #ffffff)',
        border: '1px solid var(--comment-border, #e5e7eb)',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        minWidth: '280px',
        maxWidth: '400px'
      }}
    >
      {/* Textarea */}
      <div style={{ marginBottom: '8px' }}>
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={isSubmitting}
          style={{
            width: '100%',
            minHeight: '60px',
            maxHeight: '200px',
            padding: '8px',
            border: `1px solid ${isOverLimit ? '#ef4444' : '#d1d5db'}`,
            borderRadius: '6px',
            fontSize: '14px',
            lineHeight: '1.5',
            resize: 'none',
            fontFamily: 'inherit',
            backgroundColor: 'transparent',
            color: 'var(--comment-text, #374151)',
            outline: 'none',
            transition: 'border-color 0.2s ease'
          }}
        />
        
        {/* Character count */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '4px',
            fontSize: '12px',
            color: isOverLimit ? '#ef4444' : 'var(--comment-meta, #6b7280)'
          }}
        >
          <span>
            {isReply ? 'Replying...' : 'Add a comment'}
          </span>
          <span>
            {characterCount}/{maxLength}
          </span>
        </div>
      </div>

      {/* Action buttons */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px'
        }}
      >
        <button
          type="button"
          onClick={handleCancel}
          disabled={isSubmitting}
          style={{
            padding: '6px 12px',
            border: '1px solid var(--comment-border, #e5e7eb)',
            borderRadius: '6px',
            backgroundColor: 'transparent',
            color: 'var(--comment-text, #374151)',
            fontSize: '14px',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: isSubmitting ? 0.5 : 1
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.target.style.backgroundColor = 'var(--comment-border, #e5e7eb)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
        >
          {cancelLabel}
        </button>
        
        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            padding: '6px 16px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: canSubmit 
              ? 'var(--comment-accent, #3b82f6)' 
              : 'var(--comment-border, #e5e7eb)',
            color: canSubmit ? '#ffffff' : 'var(--comment-meta, #6b7280)',
            fontSize: '14px',
            fontWeight: '500',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease',
            opacity: isSubmitting ? 0.7 : 1
          }}
        >
          {isSubmitting ? 'Posting...' : submitLabel}
        </button>
      </div>

      {/* Keyboard shortcut hint */}
      <div
        style={{
          marginTop: '8px',
          fontSize: '11px',
          color: 'var(--comment-meta, #6b7280)',
          textAlign: 'center'
        }}
      >
        {isReply ? '⌘+Enter to reply' : '⌘+Enter to comment'}
      </div>
    </form>
  );
};

CommentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  initialContent: PropTypes.string,
  isReply: PropTypes.bool,
  placeholder: PropTypes.string,
  submitLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  className: PropTypes.string,
  maxLength: PropTypes.number
};

export default CommentForm;
